import React, { useEffect, useId, useRef, useState } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { StyledPrimeReactInput } from 'styles/v2/Styles.styled';
import useIsReadOnlyMode from '../compiler/hooks/useIsReadOnlyMode';
import SmartLabel, { TSmartLabelProps } from '../labels/SmartLabel';
import useRejectOutOfRange from './useRejectOutOfRange';

type Props = Partial<TSmartLabelProps> & {
  defaultValue: number | null;
  onChanged(name: string, newValue: number | null): void;
  name: string;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  maxFractionDigits?: number;
  /** Lower bound. PrimeReact clamps to it, unless rejectOutOfRange is set. */
  min?: number;
  /** Upper bound, see min. */
  max?: number;
  /**
   * When true, a typed, pasted or stepped value outside min/max, or with more than maxFractionDigits decimals,
   * is rejected (the input keeps its previous text) instead of clamped or rounded. Never implied by min or max.
   */
  rejectOutOfRange?: boolean;
};

const WizardNumberInput: React.FC<Props> = ({
  defaultValue,
  onChanged,
  name,
  label,
  required,
  labelStyle,
  tooltip,
  tooltipPosition,
  error,
  placeholder,
  id,
  disabled,
  maxFractionDigits = 2,
  min,
  max,
  rejectOutOfRange = false,
}) => {
  const fallbackInputId = useId();
  const inputId = id || fallbackInputId;
  const isReadOnly = useIsReadOnlyMode() || disabled;

  const [value, setValue] = useState<number | null>(defaultValue);
  const latestValueRef = useRef<number | null>(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
    // A defaultValue that arrives after mount must also reach the ref, otherwise a focus and blur
    // without typing would send the stale value.
    latestValueRef.current = defaultValue;
  }, [defaultValue]);

  const { wrapperProps, inputProps } = useRejectOutOfRange({ enabled: rejectOutOfRange, min, max, maxFractionDigits });

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }} {...wrapperProps}>
      {label && (
        <SmartLabel
          label={label}
          required={required}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          error={error}
          hasValue={defaultValue !== null}
        />
      )}
      <StyledPrimeReactInput
        as={InputNumber}
        inputId={inputId}
        value={value}
        {...inputProps}
        onValueChange={(e) => {
          setValue(e.value ?? null);
          latestValueRef.current = e.value ?? null;
        }}
        onBlur={() => {
          onChanged(name, latestValueRef.current);
        }}
        disabled={isReadOnly}
        placeholder={isReadOnly ? undefined : placeholder}
        useGrouping={false}
        maxFractionDigits={maxFractionDigits}
        // InputNumber merges props with Object.assign, so an explicit undefined would overwrite its
        // default null (and make a minus sign impossible to type). Pass the bounds only when set.
        {...(min !== undefined ? { min } : {})}
        {...(max !== undefined ? { max } : {})}
      />
    </div>
  );
};

export default WizardNumberInput;
